import store from './actions/store';
import { LOGOUT, LOGIN } from './actions/types';
import { setHiddenItemsId } from "./actions/sidebarActions";
import axios from 'axios';

const SESSION_KEY = 'marketplace_session';
const IMPERSONATION_BACKUP_KEY = 'marketplace_impersonation_backup';
const INACTIVITY_LIMIT = 2 * 60 * 60 * 1000; // 2 horas en milisegundos
const ACTIVITY_WRITE_THROTTLE = 15 * 1000;

class Auth {
  constructor() {
    this.authenticated = false;
  }

  isAuthenticated = () => {
    const isAuth = store.getState().general.isAuthenticated || false;
    if (isAuth) return true;

    // Fallback para evitar rebote a login en recargas/race conditions
    const hasSession = !!window.sessionStorage.getItem(SESSION_KEY);
    const hasToken = !!window.localStorage.getItem("token");
    return hasSession && hasToken;
  }

  buildSessionData(idSeller, userName, tipoUsuario, userId, hiddenItemsIds, codProv, impersonation = null, canImpersonate = false) {
    return {
      idSeller,
      userName,
      tipoUsuario,
      userId,
      codProv,
      hiddenItemsIds: Array.isArray(hiddenItemsIds) ? hiddenItemsIds : [],
      impersonation: impersonation || null,
      canImpersonate: !!canImpersonate
    };
  }

  resolveUserName(data) {
    if (data?.userName) return data.userName;

    try {
      const token = window.localStorage.getItem("token");
      if (!token) return "";

      const payload = token.replace(/^Bearer\s+/i, "").split(".")[1];
      if (!payload) return "";

      const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
      return decoded.sub || decoded.email || "";
    } catch (e) {
      return "";
    }
  }

  applyLoginState(data) {
    const payload = {
      idSeller: data?.idSeller ?? null,
      userName: this.resolveUserName(data),
      tipoUsuario: data?.tipoUsuario ?? 0,
      userId: data?.userId ?? 0,
      codProv: data?.codProv ?? null,
      impersonation: data?.impersonation || null,
      canImpersonate: !!data?.canImpersonate
    };

    store.dispatch({
      type: LOGIN,
      payload
    });

    store.dispatch(setHiddenItemsId(Array.isArray(data?.hiddenItemsIds) ? data.hiddenItemsIds : []));
  }

  login(idSeller, userName, tipoUsuario, userId, hiddenItemsIds, codProv, impersonation = null, canImpersonate = false) {
    const sessionData = this.buildSessionData(
      idSeller,
      userName,
      tipoUsuario,
      userId,
      hiddenItemsIds,
      codProv,
      impersonation,
      canImpersonate
    );

    this.applyLoginState(sessionData);
    this.saveSession(sessionData);
  }

  loginWithSession(sessionData) {
    this.applyLoginState(sessionData);
    this.saveSession(sessionData);
  }

  setAuthToken(token) {
    const authToken = token?.startsWith?.('Bearer ') ? token : `Bearer ${token}`;
    window.localStorage.setItem("token", authToken);
    axios.defaults.headers.common['Authorization'] = authToken;
  }

  startImpersonation(sessionData, token) {
    const currentSession = window.sessionStorage.getItem(SESSION_KEY);
    const currentToken = window.localStorage.getItem("token");
    const hasBackup = !!window.sessionStorage.getItem(IMPERSONATION_BACKUP_KEY);

    if (!hasBackup && currentSession && currentToken) {
      try {
        window.sessionStorage.setItem(
          IMPERSONATION_BACKUP_KEY,
          JSON.stringify({
            session: JSON.parse(currentSession),
            token: currentToken
          })
        );
      } catch (e) {
        console.error("Error saving impersonation backup:", e);
      }
    }

    if (token) {
      this.setAuthToken(token);
    }

    this.loginWithSession(sessionData);
  }

  stopImpersonation() {
    const backupRaw = window.sessionStorage.getItem(IMPERSONATION_BACKUP_KEY);
    if (!backupRaw) {
      this.logout();
      return false;
    }

    try {
      const backup = JSON.parse(backupRaw);
      if (!backup?.session || !backup?.token) {
        this.logout();
        return false;
      }

      this.setAuthToken(backup.token);
      this.applyLoginState(backup.session);
      this.saveSession(backup.session);
      window.sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
      return true;
    } catch (e) {
      console.error("Error restoring impersonation session:", e);
      this.logout();
      return false;
    }
  }

  isImpersonating() {
    try {
      const session = window.sessionStorage.getItem(SESSION_KEY);
      if (!session) return false;
      const data = JSON.parse(session);
      return !!data?.impersonation?.isActive;
    } catch (e) {
      return false;
    }
  }

  logout() {
    this.clearSession();
    store.dispatch({
      type: LOGOUT,
      payload: false,
    });
  }

  saveSession(data) {
    const sessionData = {
      ...data,
      lastActivity: Date.now()
    };
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
    } catch (e) {
      console.error("Error saving to sessionStorage:", e);
    }
  }

  clearSession() {
    window.sessionStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
    window.localStorage.removeItem("token");
    delete axios.defaults.headers.common['Authorization'];
  }

  updateActivity() {
    const session = window.sessionStorage.getItem(SESSION_KEY);
    if (!session) return;

    try {
      const data = JSON.parse(session);
      const now = Date.now();

      // No revivir una sesión ya vencida: la resuelve checkSession
      if (now - data.lastActivity > INACTIVITY_LIMIT) return;

      // Evita reescribir sessionStorage en cada scroll/mousedown
      if (now - data.lastActivity < ACTIVITY_WRITE_THROTTLE) return;

      data.lastActivity = now;
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } catch (e) {
      console.error("Error updating activity", e);
    }
  }

  // Combina la lógica de initSession y checkSession
  // Devuelve true si la sesión se cerró por vencimiento/inconsistencia
  checkSession() {
    const session = window.sessionStorage.getItem(SESSION_KEY);
    const token = window.localStorage.getItem("token");

    if (!session || !token) {
      // Si el store seguía autenticado pero la sesión/token desapareció, cerrar para que la UI vuelva al login
      if (store.getState().general.isAuthenticated) {
        this.logout();
        return true;
      }
      return false;
    }

    try {
      const data = JSON.parse(session);
      const now = Date.now();

      if (now - data.lastActivity > INACTIVITY_LIMIT) {
        this.logout();
        return true;
      }

      axios.defaults.headers.common['Authorization'] = token;

      // Solo restaurar el estado si aún no está cargado (recarga de página);
      // evita re-dispatch de LOGIN y pisar el estado actual cada minuto.
      if (!store.getState().general.isAuthenticated) {
        this.applyLoginState(data);
      }

      this.updateActivity();
      return false;
    } catch (e) {
      console.error("Error restoring session", e);
      this.logout();
      return true;
    }
  }
}

export default new Auth();
